import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { FormControl, FormHelperText, OutlinedInput } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Link from '@mui/material/Link';

const columns = [
  { id: 'client_phone', label: 'Client Phone No' },
  { id: 'client_code', label: 'Client Code' },
  { id: 'client_name', label: 'Client Name' },
  { id: 'rm_name', label: 'RM Name' },
  { id: 'rm_phone', label: 'RM Phone No' },
  { id: 'updated_on', label: 'Updated On' },
  // { id: 'action', label: 'Action' },
];

const CustomersTable = ({ searchInput, handleEditClick, onViewClient }) => {
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputVar, setSearchInputVar] = useState('');
  const userRole = localStorage.getItem('role');
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20
  });

  const [showClientDetails, setShowClientDetails] = useState(false);
  const [selectedClientCode, setSelectedClientCode] = useState(null);

  const permissions = localStorage.getItem("permissions") || [];
  const clientMasterReadPermission = "client_master.view";
  const clientMasterAddPermission = "client_master.add";
  const clientMasterEditPermission = "client_master.change";

  const hasReadPermission = permissions.includes(clientMasterReadPermission);
  const hasAddPermission = permissions.includes(clientMasterAddPermission);
  const hasEditPermission = permissions.includes(clientMasterEditPermission);

  const URL = process.env.NODE_ENV === 'production' ? 'api' : `http://localhost:8000/api`;

  useEffect(() => {
    setSearchInputVar(searchInput);
    setController({
      page: 0,
      rowsPerPage: 20
    });
  }, [searchInput]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let searchURL = '';
        if (searchInput) {
          searchURL = `&search_value=${searchInput}`;
        }

        const user = JSON.parse(localStorage.getItem("user"));
        const tenant = localStorage.getItem('tenant');
        const headers = {
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant
        };

        const response = await fetch(
          `${URL}/clients/clients/?user_id=${user.id}&limit=${controller.rowsPerPage}&offset=${controller.page * controller.rowsPerPage}${searchURL}`, {headers}
        );
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const jsonData = await response.json();
        setHoldings(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error('Error fetching data:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [controller, searchInput]);

  const handlePageChange = (event, newPage) => {
    setController({
      ...controller,
      page: newPage
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...controller,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
  };

  return (
    <Paper sx={{ width: 'auto !important', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 490 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align="left"
                  style={{
                    minWidth: column.minWidth,
                    border: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  <Typography variant="h6">
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : holdings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No match found
                </TableCell>
              </TableRow>
            ) : (
              holdings.map((client, index) => (
                <TableRow key={index} cid={client.id}>
                  <TableCell align="left" style={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                    {client.phone_no}
                  </TableCell>
                  <TableCell align="left" style={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                    {/* {client.client_code} */}
                    <Link 
                      href="#" 
                      onClick={() => onViewClient(client.client_code)}
                      underline="none" 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    >
                      {client.client_code || 'N/A'}
                    </Link>
                  </TableCell>
                  <TableCell align="left" style={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                    {client.client_name}
                  </TableCell>
                  <TableCell align="left" style={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                    {client.reporting_manager_name}
                  </TableCell>
                  <TableCell align="left" style={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                    {client.reporting_manager}
                  </TableCell>
                  <TableCell 
                    align="left" 
                    style={{ 
                      border: '1px solid rgba(224, 224, 224, 1)',
                      display: 'flex', 
                      justifyContent: 'space-between' 
                    }}
                  >
                    {client.updated_at}
                    { hasEditPermission && ( 
                      <Button onClick={(e) => handleEditClick(e, client)}>
                        <EditIcon />
                      </Button>
                    ) }
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20]}
        component="div"
        count={rowCount}
        rowsPerPage={controller.rowsPerPage}
        page={controller.page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}

export default CustomersTable;
